'use client';

import { useRef } from 'react';

import { Button, type ButtonProps } from '@/app/components/button';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { generateSources } from '@/app/components/media/image/generate-sources';
import { type ImageProps } from '@/app/components/media/image/types';
import styles from '@/app/cv/components/sections/home/style.module.css';
import { useInView } from '@/app/hooks/useInView';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

export interface SectionLCSProps {
  dataTrack?: string;
  dataTest?: string;
  bg: Omit<ImageProps, 'aspectRatio'>;
  bgDesktop: Omit<ImageProps, 'aspectRatio'>;
  logoTop: Omit<ImageProps, 'aspectRatio'>;
  logo: Omit<ImageProps, 'aspectRatio'>;
  logoBottom: Omit<ImageProps, 'aspectRatio'>;
  subtitle: string;
  title: string;
  description: string;
  descriptionHighlight: string;
  button: Pick<ButtonProps, 'href' | 'children'>;
}

export function SectionLCS({
  dataTrack,
  dataTest,
  bg,
  bgDesktop,
  logoTop,
  logo,
  logoBottom,
  subtitle,
  title,
  description,
  descriptionHighlight,
  button,
}: SectionLCSProps) {
  const logoRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(logoRef, {
    rootMargin: '0px 0px -25% 0px',
  });

  return (
    <Section id="lcs" size="md" className="scroll-mt-14" data-test={dataTest}>
      <Container>
        <div className="aspect-9-16 md:aspect-16-9 relative grid grid-cols-1 items-start overflow-hidden text-center md:grid-cols-2 md:items-center">
          <div
            className={cn(
              'pointer-events-none absolute inset-0 -z-1 overflow-hidden rounded-md',
              styles.revealTransition,
              'duration-1200',
              {
                'scale-130 opacity-0': !isInView,
              }
            )}
          >
            <Image
              src={bg.src}
              alt={bg.alt}
              width={bg.width}
              height={bg.height}
              layout="fill"
              objectFit="cover"
              sources={generateSources('md', {
                mobileSrc: bg.src,
                mobileOptions: { crop: '9:16' },
                desktopSrc: bgDesktop.src,
                desktopOptions: { crop: '16:9' },
              })}
            />
          </div>
          <div className="mx-auto max-w-119 py-4 lg:py-0">
            <div
              ref={logoRef}
              className={cn(
                'relative m-auto max-w-42 lg:max-w-76',
                styles.logoAspect
              )}
            >
              <div
                className={cn(
                  'absolute top-0 right-0',
                  styles.logoTopSize,
                  styles.revealTransition,
                  'delay-200 duration-600',
                  {
                    '-translate-x-5 translate-y-5 opacity-0': !isInView,
                  }
                )}
              >
                <Image
                  src={logoTop.src}
                  alt={logoTop.alt}
                  width={logoTop.width}
                  height={logoTop.height}
                  aspectRatio="calculate"
                />
              </div>
              <div
                className={cn(
                  'absolute left-0',
                  styles.logoCenterPosition,
                  styles.revealTransition,
                  'duration-600',
                  {
                    'translate-y-5 opacity-0': !isInView,
                  }
                )}
              >
                <Image
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  aspectRatio="calculate"
                />
              </div>
              <div
                className={cn(
                  'absolute bottom-0',
                  styles.logoBottomPosition,
                  styles.revealTransition,
                  'delay-200 duration-600',
                  {
                    'translate-x-5 -translate-y-5 opacity-0': !isInView,
                  }
                )}
              >
                <Image
                  src={logoBottom.src}
                  alt={logoBottom.alt}
                  width={logoBottom.width}
                  height={logoBottom.height}
                  aspectRatio="calculate"
                />
              </div>
            </div>
            <h2
              className={cn(
                'font-noto-bold text-sub-h2 mt-1 text-gray-700 lg:mt-2',
                styles.revealTransition,
                'delay-500 duration-600',
                {
                  '-translate-y-5 opacity-0': !isInView,
                }
              )}
              {...createDataAttributes({ test: 'subtitle' })}
            >
              {subtitle}
            </h2>
            <div
              className={cn(
                'mx-auto mt-4 px-4 lg:mt-8',
                styles.revealTransition,
                'delay-800 duration-600',
                {
                  '-translate-y-3 opacity-0': !isInView,
                }
              )}
            >
              <h3
                className="font-noto-bold text-sub-h1 text-red-500"
                {...createDataAttributes({ test: 'title' })}
              >
                {title}
              </h3>
              <p
                className="text-gray-700"
                {...createDataAttributes({ test: 'description' })}
              >
                {description} <br className="block lg:hidden" />
                <strong
                  dangerouslySetInnerHTML={{ __html: descriptionHighlight }}
                />
              </p>
              <Button
                href={button.href}
                className="mt-4 lg:mt-6"
                {...createDataAttributes({
                  track: dataTrack,
                  section: dataTest,
                  text:
                    typeof button.children === 'string'
                      ? button.children
                      : undefined,
                  url: button.href,
                  test: 'button-cta',
                })}
                {...createDataGA({
                  click: true,
                  eventName: 'cv-lcs-button-click',
                  group: 'lcs',
                  url: button.href,
                  text: button.children,
                })}
              >
                {button.children}
              </Button>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
